import { Flex } from '@sequencehq/core-components'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import PageError from 'components/ErrorPages/PageError'
import { useSelectedCurrency } from 'RevenueRecognition/view/utils/useSelectedCurrency'
import { useSelectedDateRange } from 'RevenueRecognition/view/utils/useSelectedDateRange'
import { format, startOfYear } from '@sequencehq/utils/dates'
import { useRevRecOverviewData } from '../../utils/useRevRecOverviewData'
import { OverviewCustomers } from './OverviewCustomers'
import { OverviewNavigationCards } from './OverviewNavigation'
import { OverviewArAging } from './OverviewArAging'
import { OverviewRevenueBarChartCard } from './cards/OverviewRevenueBarChartCard'

export const OverviewContent = () => {
  const { currency } = useSelectedCurrency()

  const now = new Date()
  const defaultRange = [startOfYear(now), now]

  const { dateRange } = useSelectedDateRange({
    defaultDateRange: defaultRange as [Date, Date]
  })

  const allOverviewData = useRevRecOverviewData({
    currency,
    startDate: format(dateRange[0], 'yyyy-MM-dd'),
    endDate: format(dateRange[1], 'yyyy-MM-dd')
  })

  const overviewData = allOverviewData.overview

  const currencyWithFallback = currency ?? 'USD'

  const calculateRunningBalance = (
    monthlyData: Array<{ year: number; month: number; deferredAmount: number }>
  ) => {
    // Convert monthly deferred revenue activity to running balances
    // Backend returns monthly changes (e.g., +$1000 Jan, -$375 Feb)
    // Frontend converts to cumulative balances (e.g., $1000 Jan, $625 Feb)
    // This shows actual month-end deferred revenue balances for finance reporting
    const sortedData = [...monthlyData].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year
      return a.month - b.month
    })

    let runningBalance = 0
    return sortedData.map(dataPoint => {
      runningBalance += dataPoint.deferredAmount
      return {
        date: `${dataPoint.year}-${String(dataPoint.month).padStart(2, '0')}-01`,
        value: runningBalance // Running balance: sum of all activity up to this month
      }
    })
  }

  const recognizedChartData =
    overviewData.data?.monthlyRevenue.data.map(dataPoint => ({
      date: `${dataPoint.year}-${String(dataPoint.month).padStart(2, '0')}-01`,
      value: dataPoint.recognizedAmount
    })) ?? []

  const deferredChartData = overviewData.data?.monthlyDeferrals.data
    ? calculateRunningBalance(overviewData.data.monthlyDeferrals.data)
    : []

  return (
    <Flex padding="16px" gap="16px" maxWidth="1300px" flexWrap="wrap">
      <ErrorBoundary
        name="OverviewContent.recognizedRevenueChart"
        fallback={<PageError />}
      >
        <OverviewRevenueBarChartCard
          type="recognized"
          total={overviewData.data?.summary.totalRecognized ?? 0}
          isLoading={allOverviewData.isLoading}
          chartData={recognizedChartData}
          currency={currencyWithFallback}
          dateRange={dateRange}
        />
      </ErrorBoundary>

      <ErrorBoundary
        name="OverviewContent.deferredRevenueChart"
        fallback={<PageError />}
      >
        <OverviewRevenueBarChartCard
          type="deferred"
          total={overviewData.data?.summary.totalDeferred ?? 0}
          isLoading={allOverviewData.isLoading}
          chartData={deferredChartData}
          currency={currencyWithFallback}
          dateRange={dateRange}
        />
      </ErrorBoundary>

      <ErrorBoundary name="OverviewContent.customers" fallback={<PageError />}>
        <OverviewCustomers
          isLoading={overviewData.isLoading}
          customers={overviewData.data?.topCustomers.customers ?? []}
          currency={currencyWithFallback}
          dateRange={dateRange}
        />
      </ErrorBoundary>

      <ErrorBoundary name="OverviewContent.arAging" fallback={<PageError />}>
        <OverviewArAging key={currency} currency={currency} />
      </ErrorBoundary>

      <Flex flexDirection="row" gap="16px">
        <ErrorBoundary
          name="OverviewContent.navigationCards"
          fallback={<PageError />}
        >
          <Flex flexDirection="column" gap="16px">
            <OverviewNavigationCards.JournalReports />
            <OverviewNavigationCards.DeferredRevenue />
          </Flex>
          <OverviewNavigationCards.RevenueWaterfall />
        </ErrorBoundary>
      </Flex>
    </Flex>
  )
}
